import React, { useState, useRef, useEffect } from 'react';
import { Button, Group, Stack } from '@mantine/core';
import VideoPlayer from '../VideoPlayer/VideoPlayer';
import { MediaItem, UploadResult, MediaSaveRespose, ModalData } from '../../types';
import { useAspectRatio } from '../../hooks/useAspectRatio';
import { getAspectRatioDisplay } from '../../utils/aspectRatioDetector';
import VideoTimeline from '../VideoTimeline/VideoTimeline';
import EditSegmentTabs from '../RightTabList/EditSegmentTabs';
import Navigation from '../Home/Navigation';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styles from './VideoEditor.module.css';
import { uploadProjectMedia } from '../../services/mediaService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faArrowLeft, faPencilAlt, faWandMagicSparkles, faBroom, faMagicWandSparkles } from '@fortawesome/free-solid-svg-icons'; 
import { downloadVideo } from '../../services/autoDownloadService';
import { getApiUrl } from '../../config/api';
import { saveMediaItemToJson, captureVideoSnapshot } from './mediaItemService';
import loaderStyles from '../../assets/loader.module.css';
import VerticalNav from '../Home/VerticalNav';
import { useNotification } from '../../context/NotificationContext';
import VideoModal from '../Modal/ModelVideo';
import { useTitleGeneration } from '../../context/TitleGenerationContext';
import { useThumbnailModal } from '../../context/ThumbnailContext';
import { StepIndicatorNames } from '../../types';
import Dashboard from '../Dashboard/Dashboard';
import { projectService } from '../../services/projectService';
import AIImageModal from '../Modal/AIImageModalFresh';
import { useAuth } from '../../context/Auth';
import { autoGenerateLibraryMedia } from './fetchMediaFromLibraryService';
import { useAspectRatioConversion } from '../../hooks/useAspectRatioConversion';
import AspectRatioSplitButton from './AspectRatioSplitButton';
import CloneProjectButton from './CloneProjectButton';
import CleanupModal from './CleanupModal';
import { CleanupConfig } from '../../services/videoCleanupService';
import { 
  handleStartCleanup, 
  handleCleanupComplete
} from './cleanupHandlers';
import { handleMediaUpload } from './mediaUploadHandlers';
import { handleAIImageGenerated, handleAIVideoGenerated } from './aiGenerationHandlers';
import { 
  handleSaveMedia,
  handleSaveMediaTextToJson 
} from './projectHandlers';
import { useVideoSegmentLoading } from '../../hooks/useVideoSegmentLoading';
import ActionButtons from './ActionButtons';
import LeftPanel from './LeftPanel';
import { useProjectData } from '../../hooks/useProjectData';
import { useMediaItems } from '../../hooks/useMediaItems';
import { useVideoProcessing, QualityLevel } from '../../hooks/useVideoProcessing';
import { useVideoPlayerState } from '../../hooks/useVideoPlayerState';
import { createNavigationHandlers } from './handlers/navigationHandlers';
import { useEditorActionHandlers } from './useEditorActionHandlers';
import DescriptionEditor from './DescriptionEditor';
import { useTranslation } from 'react-i18next';
import { useMobileWarning } from '../../hooks/useMobileWarning';
import MobileWarningModal from '../EnhancedHome/MobileWarningModal';
import { switchVideoSource } from '../../utils/videoCleanup'; // ✅ Import video cleanup utility

const VideoEditor: React.FC = () => {
  const { t } = useTranslation('videoEditor');
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  
  // Mobile warning hook
  const { showMobileWarning, handleMobileWarningClose, handleMobileWarningContinue } = useMobileWarning();
  
  // Track if navigation is from back button (to bypass confirmation)
  const isIntentionalNavigation = useRef(false);
  
  // Extract projectId from URL early
  const projectIdFromUrl = searchParams.get("project-id") || "";
  const projectId = useRef(projectIdFromUrl);
  
  // Update projectId ref when URL changes
  useEffect(() => {
    if (projectIdFromUrl) {
      projectId.current = projectIdFromUrl;
    }
  }, [projectIdFromUrl]);
  
  // Global state for media editing
  const [addAtCurrentTime, setAddAtCurrentTime] = useState<boolean>(false);

  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState<ModalData | null>(null);
  
  // Video cleanup state
  const [cleanupJobId, setCleanupJobId] = useState<string | null>(null);
  const [isCleanupModalOpen, setIsCleanupModalOpen] = useState(false); // Back to false - no auto-open
  
  // Job processing state - tracks if any background job is running
  const [isJobProcessing, setIsJobProcessing] = useState<boolean>(false);
  
  // ✅ NEW: Crop/Edit mode state (Phase 3)
  const [isCropEditActive, setIsCropEditActive] = useState(false);
  
  // ✅ NEW: Watermark state
  const isFreePlan = user?.subscriptionPlan === 'Creator (Free)';
  const [includeWatermark, setIncludeWatermark] = useState<boolean>(isFreePlan);

  // Sync watermark state with user plan changes
  useEffect(() => {
    setIncludeWatermark(isFreePlan);
  }, [isFreePlan]);
  
  // NEW: Video segmentation state
  const {
    loadedSegmentId,
    loadedSegmentUrl,
    isLoadingSegment,
    loadSegment,
    clearLoadedSegment,
    isSegmentLoaded,
  } = useVideoSegmentLoading();
  
  // REFACTORED: Use aspect ratio hook with manual control instead of auto-detect
  const {
    aspectRatio,
    result: aspectRatioResult,
    isDetecting: isDetectingAspectRatio,
    displayInfo,
    detect: detectAspectRatio
  } = useAspectRatio(undefined, {
    autoDetect: false, // Disable auto-detect
    fallbackRatio: '16:9'
  });

  // NEW: Aspect ratio conversion hook
  const {
    isConversionMode,
    targetAspectRatio,
    startConversion,
    cancelConversion,
    initializeMediaItems,
    resetMediaItem,
    updateMediaItemPosition,
    updateMediaItemScale
  } = useAspectRatioConversion();
  
  // Check if coming from AI Video Editor
  const fromApp = searchParams.get('from_app');
  const pageTitle = fromApp === 'ai-video-editor' 
    ? t('videoEditor.pageTitle.aiVideoEditor')
    : t('videoEditor.pageTitle.createContent');
  
  // **VIDEO EDITOR MODES**
  // This component supports two distinct modes:
  // 
  // 1. AUDIO + MEDIA MODE (audioUrl exists, videoUrl = null, hasVideoAudio = false):
  //    - Background audio plays continuously from audioUrl
  //    - Media items (images/videos) appear on timeline as overlays
  //    - Timeline shows green audio waveform bar
  //    - Videos in media items are muted (audio comes from background track)
  //
  // 2. VIDEO-ONLY MODE (videoUrl exists, audioUrl = null, hasVideoAudio = true):
  //    - NO background audio track
  //    - Only video clips in media items play
  //    - Timeline shows NO audio bar (just media items)
  //    - Videos in media items play WITH audio (not muted)
  //    - Gaps between video clips = silence
  //
  let refLinkDownload = useRef<HTMLAnchorElement>(null);
  let saveLabel = useRef(t('videoEditor.actionButtons.save'));
  const [disabledBtnPendingDownload, setDisabledBtnPendingDownload] = useState<boolean>(false);
  const [isAIImageModalOpen, setIsAIImageModalOpen] = useState<boolean>(false);
  const [aiModalInitialValues, setAIModalInitialValues] = useState<{
    tab?: 'image' | 'video' | 'upscale';
    imageUrl?: string;
    videoUrl?: string;
    prompt?: string;
    initialProvider?: 'runway' | 'ltx' | 'veo'; // Add initialProvider prop
  } | null>(null);

  const { showNotification } = useNotification();
  const { openTitleModal } = useTitleGeneration();
  const { openThumbnailModal } = useThumbnailModal();  

  // ✅ Custom hooks for state management
  const {
    project,
    setProject,
    projectTitle,
    setProjectTitle,
    projectDescription,
    setProjectDescription,
    isEditingTitle,
    setIsEditingTitle,
    updateTitle,
    updateDescription,
    generatedVideoUrl,
    setGeneratedVideoUrl,
    downloadLink,
    isVideoSegmented,
    setIsVideoSegmented
  } = useProjectData({
    projectId: projectId.current,
    showNotification,
    setDisabledBtnPendingDownload
  });

  const {
    mediaItems,
    setMediaItems,
    initialMediaItems,
    setInitialMediaItems,
    allMediaItemsFromDB,
    setAllMediaItemsFromDB,
    selectedMediaItemId,
    setSelectedMediaItemId,
    videoSegments,
    isTimelineLoading,
    setIsTimelineLoading,
    splitMediaItem: splitMediaItemHook,
    reloadMediaItems
  } = useMediaItems({
    projectId: projectId.current,
    audioUrl: searchParams.get('audio-url') || project?.finalAudio || project?.originalAudioUrl ,
    videoUrl: searchParams.get('video-url') || project?.originalVideoUrl,
    isVideoSegmented,
    showNotification,
    detectAspectRatio,
    onLoadComplete: () => setDisabledBtnPendingDownload(false)
  });

  // Initialize video player state BEFORE useVideoProcessing so audioUrl is available
  const {
    currentTime,
    setCurrentTime,
    mediaCurrentTime,
    setMediaCurrentTime,
    duration,
    setDuration,
    audioUrl,
    videoUrl,
    hasVideoAudio,
    audioRef,
    videoRef,
    handleAudioTimeUpdate,
    handleLoadedMetadata,
    handleSeek,
    handleTimeUpdate,
    handleDurationChange,
    handleVideoPlay,
    handleVideoPause
  } = useVideoPlayerState({
    audioUrlParam: searchParams.get('audio-url') || project?.finalAudio || project?.originalAudioUrl,
    videoUrlParam: searchParams.get('video-url'),
    project
  });

  const {
    isProcessing,
    setIsProcessing,
    isPreparingDownload,
    setIsPreparingDownload,
    mediaJobId,
    showMediaDashboard,
    setShowMediaDashboard,
    generateMedia
  } = useVideoProcessing({
    projectId: projectId.current,
    audioUrl, // ✅ Now properly passed from useVideoPlayerState
    videoUrl, // ✅ Pass videoUrl for video-only mode
    aspectRatio,
    isConversionMode,
    targetAspectRatio,
    includeWatermark,
    showNotification
  });

  // ✅ Editor action handlers (media transform + AI modals)
  const {
    handleUpdateMediaPosition,
    handleUpdateMediaScale,
    handleResetMediaTransform,
    handleOpenAIVideoFromImage,
    handleOpenUpscaleFromVideo,
    handleVideoUpscaled
  } = useEditorActionHandlers({
    setMediaItems,
    setAIModalInitialValues,
    setIsAIImageModalOpen,
    updateMediaItemPosition,
    updateMediaItemScale,
    showNotification
  });

  // Log aspect ratio changes
  useEffect(() => {
    if (aspectRatioResult?.source === 'existing-element' || aspectRatioResult?.source === 'metadata') {
      console.log('🎯 [ASPECT RATIO STATE] Updated to:', aspectRatio);
      console.log('🎯 [ASPECT RATIO DETAILS]', aspectRatioResult);
    }
  }, [aspectRatio, aspectRatioResult]);

  // Prevent browser back navigation (two-finger swipe) with confirmation
  useEffect(() => {
    // Handle page refresh/close
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Only show warning if there are unsaved changes (mediaItems exist)
      if (mediaItems.length > 0) {
        e.preventDefault();
        e.returnValue = ''; // Chrome requires returnValue to be set
      }
    };

    // Handle browser back/forward navigation (including two-finger swipe)
    const handlePopState = (e: PopStateEvent) => {
      // Skip confirmation if user clicked the back button
      if (isIntentionalNavigation.current) {
        isIntentionalNavigation.current = false;
        return;
      }
      
      if (mediaItems.length > 0) {
        const confirmLeave = window.confirm(
          'Are you sure you want to leave? Any unsaved changes will be lost.'
        );
        
        if (!confirmLeave) {
          // Push state back to prevent navigation
          window.history.pushState(null, '', window.location.href);
        }
      }
    };

    // Push initial state to enable back button detection
    window.history.pushState(null, '', window.location.href);
    
    // Add listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [mediaItems]); // Re-run when mediaItems changes


  const handleGenerateVideoForModal = () => {
    handleGenerateMedia(true);
  };

  const handleModalCompletionCallback = (videoUrl: string, projectId: string) => {
    downloadLink.current = videoUrl;
    setGeneratedVideoUrl(videoUrl);
  };

  const handleMediaCreationComplete = (videoUrl: string, projectId: string) => {
    downloadLink.current = videoUrl;
    setGeneratedVideoUrl(videoUrl);
    setIsPreparingDownload(true); // ✅ Show preparing state
    setShowMediaDashboard(false); // Hide dashboard immediately
    
    // showNotification({ 
    //   type: 'info', 
    //   message: 'Preparing your video for download... This may take a moment for large files.', 
    //   title: '⏳ Preparing Download' 
    // });
    
    downloadVideo(videoUrl, {
      filename: `video-${Date.now()}.mp4`,
      onComplete: () => {
        setIsPreparingDownload(false);
        setDisabledBtnPendingDownload(false);
        // showNotification({ 
        //   type: 'success', 
        //   message: 'Video download started! Check your Downloads folder.', 
        //   title: '✅ Download Ready' 
        // });
      },
      onError: (error: any) => {
        setIsPreparingDownload(false);
        setDisabledBtnPendingDownload(false);
        showNotification({ 
          type: 'error', 
          message: `Download preparation failed: ${error}`, 
          title: 'Error' 
        });
      }
    });
  };

  const handleAutoGenerateLibraryMedia = async () => {
    if (!projectId.current || !audioRef.current) {
      showNotification({ type: 'error', message: 'Missing project ID or audio reference', title: 'Error' });
      return;
    }

    try {
      setIsProcessing((prev) => ({ ...prev, autoGenerateLibrary: true }));
      setDisabledBtnPendingDownload(true);

      const token = localStorage.getItem('authToken');
      const generatedItems = await autoGenerateLibraryMedia(
        projectId.current,
        audioRef,
        setMediaItems,
        token || undefined
      );

      if (generatedItems && generatedItems.length > 0) {
        //showNotification({ type: 'success', message: `Successfully generated ${generatedItems.length} media items from library!`, title: 'Success' });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      showNotification({ type: 'error', message: `Failed to auto-generate media: ${errorMessage}`, title: 'Error' });
    } finally {
      setIsProcessing((prev) => ({ ...prev, autoGenerateLibrary: false }));
      setDisabledBtnPendingDownload(false);
    }
  };

  const handleCloseModal = () => {
    setModalData(null);
  };

  // ✅ Wrapper for split media item
  const onSplitMediaItem = (itemId: string, splitTime?: number) => {
    splitMediaItemHook(itemId, splitTime, currentTime);
  };

  // ✅ Wrapper for generate media
  const handleGenerateMedia = async (save: boolean, quality?: QualityLevel) => {
    await generateMedia(mediaItems, save, quality, setMediaItems);
  };

  // ✅ Navigation handler
  const handleNavSelected = createNavigationHandlers(
    navigate,
    projectId.current,
    audioUrl,
    mediaItems,
    openTitleModal,
    openThumbnailModal,
    setIsModalOpen,
    showNotification,
    project
  );

  // Handle segment click - load segment on demand with proper cleanup
  const handleSegmentClick = async (segment: MediaItem) => {
    if (!isVideoSegmented) return;
    
    console.log(`🎬 Segment clicked: ${segment.name}`);
    
    try {
      // Step 1: Load segment URL
      const segmentUrl = await loadSegment(segment);
      
      // Step 2: Update media items state to mark which is loaded
      setMediaItems(prevItems => 
        prevItems.map(item => {
          if (item.type === 'video' && isVideoSegmented) {
            return { 
              ...item, 
              isLoaded: item.id === segment.id 
            };
          }
          return item;
        })
      );
      
      console.log(`✅ Segment loaded, all others unloaded`);
      
      // Step 3: Switch video source with proper cleanup
      if (videoRef.current && segmentUrl) {
        console.log(`📺 Switching video source with cleanup: ${segmentUrl}`);
        
        try {
          // ✅ Use switchVideoSource utility for proper MediaPlayer cleanup
          await switchVideoSource(videoRef.current, segmentUrl, segment.start);
          
          // Update timeline position
          setCurrentTime(segment.start);
          setMediaCurrentTime(segment.start);
          
          console.log('✅ Video source switched successfully');
        } catch (error) {
          console.error('❌ Failed to switch video source:', error);
          showNotification({
            type: 'error',
            message: `Failed to switch video: ${error}`,
            title: 'Error'
          });
        }
      }
    } catch (error) {
      console.error(`❌ Failed to load segment:`, error);
      showNotification({
        type: 'error',
        message: `Failed to load segment: ${error}`,
        title: 'Error'
      });
    }
  };

  useEffect(() => {
    const autoConvert = searchParams.get('auto_convert');
    const sourceRatio = searchParams.get('source_ratio');
    const targetRatio = searchParams.get('target_ratio');
    
    if (autoConvert === 'true' && 
        sourceRatio && 
        targetRatio && 
        sourceRatio !== targetRatio &&
        !isConversionMode) {  // Guard: don't re-activate
      
      console.log(`✅ [AUTO-CONVERT] Activating conversion: ${sourceRatio} → ${targetRatio}`);
      startConversion(sourceRatio, targetRatio);
      
      // Clean up URL params after activation
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('auto_convert');
      newParams.delete('source_ratio');
      newParams.delete('target_ratio');
      
      // Replace URL without adding to history
      navigate(`?${newParams.toString()}`, { replace: true });
    }
  }, [searchParams, isConversionMode, startConversion, navigate]);

  const [navSelected, setNavSelected] = useState<StepIndicatorNames>(StepIndicatorNames.ContentCreation);

  return (
    <div className={styles.container}>
      <Navigation />
      
      {/* Mobile Warning Modal */}
      <MobileWarningModal 
        show={showMobileWarning}
        onClose={handleMobileWarningClose}
        onContinue={handleMobileWarningContinue}
      />
      
      {/* Dashboard Processing - Flush with header */}
      {showMediaDashboard && mediaJobId && (
        <div style={{ 
          width: '100%', 
          margin: '0',
          padding: '0',
          backgroundColor: '#1a1a1a',
          minHeight: '8px',
          marginTop: '-1px'
        }}>
          <Dashboard
            jobId={mediaJobId}
            callBackFunc={handleMediaCreationComplete}
            displayOption="fullview"
            onProcessingStateChange={setIsJobProcessing}
          />
        </div>
      )}
      
      {/* Cleanup Progress Dashboard */}
      {cleanupJobId && (
        <div style={{ 
          width: '100%', 
          margin: '0',
          padding: '0',
          backgroundColor: '#1a1a1a',
          minHeight: '8px',
          marginTop: '-1px'
        }}>
          <Dashboard
            jobId={cleanupJobId}
            callBackFunc={(videoUrl: string, _dashboardProjectId: string) => {
              // ✅ FIX: Accept Dashboard callback parameters but use projectId.current
              // videoUrl is empty for cleanup jobs, projectId from callback may differ
              handleCleanupComplete(
                projectId.current,
                {
                  setCleanupJobId,
                  setIsCleanupModalOpen,
                  setDisabledBtnPendingDownload,
                  setMediaItems,
                },
                { showNotification }
              );
            }}
            displayOption="fullview"
            onProcessingStateChange={setIsJobProcessing}
          />
        </div>
      )}
      
      <VerticalNav
        handleNavSelected={handleNavSelected}
        initialStep={StepIndicatorNames.ContentCreation}
        projectData={project ? {
          originalAudioUrl: project.originalAudioUrl || audioUrl || '',
          finalAudio: project.finalAudio || audioUrl || '',
          originalVideoUrl: project.originalVideoUrl,
          generatedVideoUrl: project.generatedVideoUrl,
          captionedVideoUrl: project.captionedVideoUrl,
          thumbnail: project.thumbnail,
          title: project.title,
          hasCaptions: project.hasCaptions,
          titleData: project.titleData
        } : null}
      />
      <main className={styles.main}>
        <div>
          {/* Cleanup Modal */}
          <CleanupModal
            isOpen={isCleanupModalOpen}
            onClose={() => setIsCleanupModalOpen(false)}
            onStartCleanup={(config) =>
              handleStartCleanup(
                projectId.current, // Changed from cleanupTargetMediaItemId - send projectId instead
                config,
                {
                  setCleanupJobId,
                  setIsCleanupModalOpen,
                  setDisabledBtnPendingDownload,
                  setMediaItems,
                },
                { showNotification }
              )
            }
            projectId={projectId.current} // Changed from mediaItemId
            hasCaption={project?.hasCaptions || false} // Pass hasCaptions from project
          />
          
          <VideoModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onGenerateVideo={handleGenerateVideoForModal}
            downloadLinkRef={downloadLink}
            projectIdRef={projectId}
            isProcessing={isProcessing.saveAndDownloadMedia}
            showDashboard={showMediaDashboard}
            jobId={mediaJobId}
            onCompletionCallback={handleModalCompletionCallback}
          />

          <AIImageModal
            isOpen={isAIImageModalOpen}
            onClose={() => {
              setIsAIImageModalOpen(false);
              setAIModalInitialValues(null);
            }}
            onImageGenerated={(imageUrl, prompt) => handleAIImageGenerated(
              imageUrl,
              prompt,
              { addAtCurrentTime, currentTime, showNotification },
              setMediaItems,
              setIsAIImageModalOpen
            )}
            onVideoGenerated={(videoUrl, prompt, duration) => handleAIVideoGenerated(
              videoUrl,
              prompt,
              duration,
              { addAtCurrentTime, currentTime, showNotification },
              setMediaItems,
              setIsAIImageModalOpen
            )}
            onVideoUpscaled={handleVideoUpscaled}
            projectId={projectId.current || undefined}
            initialTab={aiModalInitialValues?.tab}
            initialImageUrl={aiModalInitialValues?.imageUrl}
            initialVideoUrl={aiModalInitialValues?.videoUrl}
            initialPrompt={aiModalInitialValues?.prompt}
            initialProvider={aiModalInitialValues?.initialProvider} // Pass initialProvider prop
          />
        </div>

        <Group style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <section>
            <div style={{ display: "flex", justifyContent: 'space-between', alignItems: "center", width: "100%" }}>
              <div style={{ display: "flex", alignItems: "flex-start", flexDirection: "column" }}>
                <div style={{ display: "flex", alignItems: "center", marginBottom: "-0.50rem" }}>
                  <Button
                    className={styles.backButton}
                    onClick={() => {
                      // Mark as intentional navigation to bypass confirmation
                      isIntentionalNavigation.current = true;
                      
                      // Navigate back based on where user came from
                      const destination = fromApp === 'ai-video-editor' 
                        ? '/ai-video-editor' 
                        : '/create-content';
                      navigate(destination);
                    }}
                    title={t('videoEditor.navigation.backToProjects')}
                  >
                    <FontAwesomeIcon icon={faArrowLeft} />
                  </Button>

                  <h1 className={styles.pageTitle}>{pageTitle}</h1>
                </div>

                <div className={styles.titleContainer} style={{ marginLeft: "4rem" }}>
                  {isEditingTitle ? (
                    <input
                      type="text"
                      value={projectTitle}
                      onChange={(e) => setProjectTitle(e.target.value)}
                      onBlur={() => updateTitle(projectTitle)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') updateTitle(projectTitle);
                        if (e.key === 'Escape') {
                          setIsEditingTitle(false);
                        }
                      }}
                      className={styles.projectTitleInput}
                      autoFocus
                    />
                  ) : (
                    <>
                      <h2 className={styles.projectTitle}>{projectTitle}</h2>
                      <button
                        className={styles.editButton}
                        onClick={() => setIsEditingTitle(true)}
                        title="Edit project title"
                      >
                        <FontAwesomeIcon style={{ fontSize: "14px" }} icon={faPencilAlt} />
                      </button>
                      <DescriptionEditor
                        description={projectDescription}
                        onSave={updateDescription}
                        disabled={disabledBtnPendingDownload}
                      />
                    </>
                  )}
                </div>
              </div>
            </div>
          </section>

          <section>
            <ActionButtons
              mediaItems={mediaItems}
              disabledBtnPendingDownload={disabledBtnPendingDownload || isJobProcessing || showMediaDashboard || !!cleanupJobId}
              isProcessing={{
                savingMedia: isProcessing.savingMedia,
                saveAndDownloadMedia: isProcessing.saveAndDownloadMedia,
              }}
              isPreparingDownload={isPreparingDownload}
              saveLabel={saveLabel}
              downloadLink={downloadLink}
              generatedVideoUrl={generatedVideoUrl}
              refLinkDownload={refLinkDownload}
              onSave={() => {
                handleSaveMediaTextToJson(
                  projectId.current,
                  audioUrl,
                  mediaItems,
                  showNotification,
                  setIsProcessing,
                  setDisabledBtnPendingDownload,
                  setMediaItems
                );
              }}
              onGenerate={(quality) => handleGenerateMedia(true, quality)}
              onUploadMedia={(e) => handleMediaUpload(
                e,
                'event',
                { addAtCurrentTime, currentTime, detectAspectRatio, showNotification },
                setMediaItems
              )}
              onOpenAIGenerator={() => setIsAIImageModalOpen(true)}
              onOpenVeoVideo={() => {
                setAIModalInitialValues({ 
                  tab: 'video',
                  initialProvider: 'veo'
                });
                setIsAIImageModalOpen(true);
              }}
              onOpenUpscale={() => {
                setAIModalInitialValues({ tab: 'upscale' });
                setIsAIImageModalOpen(true);
              }}
            />
          </section>
        </Group>

        <Stack className={styles.layout}>
          {/* LEFT COLUMN - AI & Automation Tools */}
          <LeftPanel
            mediaItems={mediaItems}
            aspectRatio={aspectRatio}
            isDetectingAspectRatio={isDetectingAspectRatio}
            aspectRatioResult={aspectRatioResult}
            isConversionMode={isConversionMode}
            targetAspectRatio={targetAspectRatio}
            onStartConversion={(target) => {
              startConversion(aspectRatio, target);
              const initialized = initializeMediaItems(mediaItems);
              setMediaItems(initialized);
              showNotification({
                type: 'info',
                title: t('videoEditor.aspectRatio.conversionMode'),
                message: t('videoEditor.aspectRatio.adjustPositions')
              });
            }}
            onCancelConversion={() => {
              cancelConversion();
              // showNotification({
              //   type: 'info',
              //   title: 'Conversion Cancelled',
              //   message: 'Returned to normal mode.'
              // });
            }}
            projectId={projectId.current}
            projectTitle={projectTitle}
            onCloneSuccess={(newProjectId, newProjectTitle, clonedTargetRatio) => {
              showNotification({
                type: 'success',
                title: t('videoEditor.notifications.projectCloned'),
                message: t('videoEditor.notifications.projectCloned')
              });
              setProjectTitle(newProjectTitle);
              
              const fromAppParam = searchParams.get('from_app');
              const params = new URLSearchParams();
              params.append('project-id', newProjectId);
              if (fromAppParam) {
                params.append('from_app', fromAppParam);
              }
              params.append('source_ratio', aspectRatio);
              params.append('target_ratio', clonedTargetRatio);
              params.append('auto_convert', 'true');

              navigate(`/create-content?${params.toString()}`);
            }}
            isProcessing={isProcessing}
            disabledBtnPendingDownload={disabledBtnPendingDownload}
            onAutoGenerateLibraryMedia={handleAutoGenerateLibraryMedia}
            includeWatermark={includeWatermark}
            onWatermarkChange={setIncludeWatermark}
            isFreePlan={isFreePlan}
            audioRef={audioRef}
            audioUrl={audioUrl}
            onAudioTimeUpdate={handleAudioTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            searchParams={searchParams}
          />

          {/* CENTER COLUMN - Video Player & Timeline */}
          <div className={styles.center}>
            {/* Show VideoPlayer when there's audio OR video (regardless of media items) or when there are media items */}
            {(audioUrl || videoUrl || hasVideoAudio || (mediaItems && mediaItems.length > 0) || isTimelineLoading || project?.originalVideoUrl) ? (
              <VideoPlayer
                mediaItems={mediaItems || []}
                video="" // In video-only mode, don't pass video as background - it exists only in media items
                captions={[]}
                videoRef={videoRef}
                onTimeUpdate={handleTimeUpdate}
                onDurationChange={handleDurationChange}
                mediaCurrentTime={mediaCurrentTime}
                setMediaCurrentTime={setMediaCurrentTime}
                onVideoPlay={handleVideoPlay}
                onVideoPause={handleVideoPause}
                project={project}
                onTimelineLoadingChange={setIsTimelineLoading}
                isConversionMode={isConversionMode || isCropEditActive}
                targetAspectRatio={targetAspectRatio}
                selectedMediaItem={mediaItems.find(item => item.id === selectedMediaItemId) || null}
                sourceAspectRatio={aspectRatio}
                onUpdateMediaPosition={handleUpdateMediaPosition}
                onUpdateMediaScale={handleUpdateMediaScale}
                onResetMediaTransform={handleResetMediaTransform}
                aspectRatio={aspectRatio}
                shouldPlayVideoAudio={hasVideoAudio}
              />
            ) : (
              <div className={styles.noVideo} >
                {!disabledBtnPendingDownload &&
                  <div className={styles.noMedia}>
                    No audio or media available
                  </div>
                }
                {disabledBtnPendingDownload &&
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <div className={loaderStyles["lds-roller"]}><div></div><div></div><div></div><div></div><div></div><div></div><div></div><div></div></div>
                    <div className={styles.noMedia}>
                      Loading Media...
                    </div>
                  </div>
                }
              </div>
            )}

            {/* Timeline - show whenever there's audio or video, regardless of media items */}
            {(audioUrl || videoUrl) && (
              <div className={styles.timelineWrapper}>
                <VideoTimeline
                  duration={duration}
                  currentTime={mediaCurrentTime}
                  segments={[]}
                  onSeek={handleSeek}
                  mediaItems={mediaItems || []}
                  setMediaItems={setMediaItems}
                  audioUrl={audioUrl || ""} // NEW: Use videoUrl as fallback for timeline
                  setModalData={setModalData}
                  setSegments={() => {}}
                  isLoading={isTimelineLoading}
                  selectedMediaItemId={selectedMediaItemId}
                  setSelectedMediaItemId={setSelectedMediaItemId}
                  addAtCurrentTime={addAtCurrentTime}
                  setAddAtCurrentTime={setAddAtCurrentTime}
                  onSplitMediaItem={onSplitMediaItem}
                  isVideoMode={hasVideoAudio} // NEW: Pass video mode flag
                  isVideoSegmented={isVideoSegmented} // NEW: Pass segmented flag
                  onSegmentClick={handleSegmentClick} // NEW: Pass segment click handler
                  onCleanupClick={() => setIsCleanupModalOpen(true)} // NEW: Pass cleanup handler
                  isCropEditActive={isCropEditActive} // NEW: Pass crop/edit state
                  onCropEditToggle={setIsCropEditActive} // NEW: Pass crop/edit toggle handler
                  disabledBtnPendingDownload={disabledBtnPendingDownload} // NEW: Pass disabled state
                  project={project} // NEW: handler for reset button
                />
              </div>
            )}
          </div>

          {/* RIGHT COLUMN - Media/Library/Prompts Tabs */}
          <div className={styles.right}>
            <EditSegmentTabs
              setCaptions={() => {}}
              modalData={modalData ? { ...modalData, onClose: handleCloseModal } : null}
              captions={[]}
              getCaptionId={() => 0}
              setMediaItems={setMediaItems}
              mediaItems={mediaItems}
              onOpenAIVideoModal={handleOpenAIVideoFromImage}
              onOpenUpscaleModal={handleOpenUpscaleFromVideo}
              projectId={projectId.current}
              addAtCurrentTime={addAtCurrentTime}
              currentTime={currentTime}
              isConversionMode={isConversionMode}
              targetAspectRatio={targetAspectRatio}
              selectedMediaItem={mediaItems.find(item => item.id === selectedMediaItemId) || null}
              onResetMediaItem={(itemId) => {
                const reset = resetMediaItem(mediaItems, itemId);
                setMediaItems(reset);
                showNotification({
                  type: 'success',
                  title: 'Reset Complete',
                  message: 'Media item reset to center position.'
                });
              }}
              allMediaItemsFromDB={allMediaItemsFromDB}
              initialMediaItems={initialMediaItems}
              onSeek={handleSeek}
              onReloadMediaItems={reloadMediaItems}
            />
          </div>
        </Stack>
      </main>
    </div>
  );
};

export default VideoEditor;